import CryptoJS from 'crypto-js';

/**
 * Service to handle AES-256 encryption and decryption.
 * Used for securing data at rest in IndexedDB and local files.
 */

/**
 * Encrypts a string or object using a secret key.
 * @param data The data to encrypt (string or object).
 * @param secretKey The secret key used for encryption.
 * @returns The encrypted data as a string.
 */
export const encrypt = (data: any, secretKey: string): string => {
    const stringData = typeof data === 'string' ? data : JSON.stringify(data);
    return CryptoJS.AES.encrypt(stringData, secretKey).toString();
};

/**
 * Decrypts an encrypted string using a secret key.
 * @param encryptedData The encrypted string.
 * @param secretKey The secret key used for decryption.
 * @returns The decrypted data (string or parsed object).
 * @throws Error if decryption fails or key is invalid.
 */
export const decrypt = (encryptedData: string, secretKey: string): any => {
    try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
        const originalText = bytes.toString(CryptoJS.enc.Utf8);

        if (!originalText) {
            throw new Error("INVALID_KEY");
        }

        try {
            return JSON.parse(originalText);
        } catch {
            return originalText;
        }
    } catch (error) {
        throw new Error("DECRYPTION_FAILED");
    }
};

/**
 * Generates a strong key derivation from a user password using PBKDF2.
 * @param password The user-provided master password.
 * @param salt Optional salt (defaults to a fixed value if not provided).
 * @returns A string representation of the derived key.
 */
export const deriveKey = (password: string, salt: string = 'gstr-reconciler-salt'): string => {
    return CryptoJS.PBKDF2(password, salt, {
        keySize: 256 / 32,
        iterations: 1000
    }).toString();
};
